/**
 * Client-Side Admin Fix
 * This script fixes the navigation and admin issues by working with the existing session
 * and avoiding the problematic areas that cause loops and 400 errors
 */

import { createClient } from '@supabase/supabase-js';

// Supabase configuration
const supabaseUrl = 'https://lcevfqxsypgtrquujsug.supabase.co';
const supabaseKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxjZXZmcXhzeXBndHJxdXVqc3VnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzYyNTgzNjcsImV4cCI6MjA1MTgzNDM2N30.KmtI1_tYz_5YJhF7lhRAOQnHAM3VGnx4vKI4DRO8KDw';

export interface FixResult {
  success: boolean;
  message: string;
  details?: any;
}

/**
 * Get the current auth token from localStorage
 */
function getAuthToken(): string | null {
  try {
    const authKeys = Object.keys(localStorage).filter(key => 
      key.includes('supabase') && key.includes('auth')
    );
    
    for (const key of authKeys) {
      const authData = JSON.parse(localStorage.getItem(key) || '{}');
      if (authData?.access_token) {
        return authData.access_token;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
}

/**
 * Create a new Supabase client instance for admin operations
 */
function createAdminClient() {
  const accessToken = getAuthToken();
  
  const client = createClient(supabaseUrl, supabaseKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
    },
    global: {
      headers: accessToken ? {
        Authorization: `Bearer ${accessToken}`
      } : {}
    }
  });
  
  return { client, hasToken: !!accessToken };
}

/**
 * Fix the admin profile with only the essential columns to avoid 400 errors
 */
export async function fixAdminProfile(): Promise<FixResult> {
  console.log('🔧 Starting admin profile fix...');
  
  try {
    const { client, hasToken } = createAdminClient();
    
    if (!hasToken) {
      return {
        success: false,
        message: 'No authentication token found. Please ensure you are logged in.'
      };
    }
    
    // Get current user to verify admin email
    const { data: userData, error: userError } = await client.auth.getUser();
    
    if (userError || !userData?.user) {
      return {
        success: false,
        message: 'Failed to get current user information.'
      };
    }
    
    const user = userData.user;
    const isAdminEmail = user.email === 'simmakin@gmail.com' || user.email === 'simmakin@googlemail.com';
    
    if (!isAdminEmail) {
      return {
        success: false,
        message: 'This fix is only for admin accounts.'
      };
    }
    
    console.log('✅ Admin email confirmed:', user.email);
    
    // Try to update profile with minimal required fields only
    const profileUpdate = {
      role: 'admin',
      subscription_status: 'active',
      subscription_end_date: new Date(Date.now() + 10 * 365 * 24 * 60 * 60 * 1000).toISOString()
    };
    
    console.log('📝 Updating profile with essential fields...');
    
    const { error: updateError } = await client
      .from('profiles')
      .update(profileUpdate)
      .eq('id', user.id);
    
    if (updateError) {
      console.log('❌ Standard update failed, trying alternative approach...');
      
      // Try direct SQL approach if standard update fails
      const { error: sqlError } = await client.rpc('update_admin_profile', {
        admin_user_id: user.id,
        admin_email: user.email
      });
      
      if (sqlError) {
        console.log('❌ SQL approach also failed, checking if profile exists...');
        
        // Check if profile exists and what its current state is
        const { data: profile, error: getError } = await client
          .from('profiles')
          .select('id, role, subscription_status, email')
          .eq('id', user.id)
          .single();
        
        if (getError) {
          return {
            success: false,
            message: `Failed to get profile: ${getError.message}`
          };
        }
        
        if (profile?.role === 'admin') {
          return {
            success: true,
            message: 'Profile is already configured correctly.',
            details: profile
          };
        }
        
        return {
          success: false,
          message: `Profile update failed: ${updateError.message}`,
          details: { profile, updateError }
        };
      }
    }
    
    // Verify the update
    const { data: updatedProfile, error: verifyError } = await client
      .from('profiles')
      .select('id, role, subscription_status, email, subscription_end_date')
      .eq('id', user.id)
      .single();
    
    if (verifyError) {
      return {
        success: false,
        message: `Update succeeded but verification failed: ${verifyError.message}`
      };
    }
    
    return {
      success: true,
      message: 'Admin profile fixed successfully!',
      details: updatedProfile
    };
    
  } catch (error) {
    console.error('❌ Error in fixAdminProfile:', error);
    return {
      success: false,
      message: `Unexpected error: ${error instanceof Error ? error.message : String(error)}`
    };
  }
}

/**
 * Clear problematic cache entries that might cause auth loops
 */
export async function clearAuthCache(): Promise<FixResult> {
  console.log('🧹 Clearing auth cache...');
  
  try {
    const keysToRemove: string[] = [];
    
    // Find all potentially problematic cache keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.includes('subscription_cache') ||
        key.includes('last_admin') ||
        key.includes('auth_state') ||
        key.includes('token_refresh')
      )) {
        keysToRemove.push(key);
      }
    }
    
    // Remove the problematic keys
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log('🗑️ Removed cache key:', key);
    });
    
    // Also clear any rate limiter state
    const rateLimiterKeys = Object.keys(sessionStorage).filter(key => 
      key.includes('auth_rate') || key.includes('circuit_breaker')
    );
    
    rateLimiterKeys.forEach(key => {
      sessionStorage.removeItem(key);
      console.log('🗑️ Removed rate limiter key:', key);
    });
    
    return {
      success: true,
      message: `Cleared ${keysToRemove.length + rateLimiterKeys.length} cache entries`,
      details: { removedKeys: [...keysToRemove, ...rateLimiterKeys] }
    };
    
  } catch (error) {
    console.error('❌ Error clearing cache:', error);
    return {
      success: false,
      message: `Failed to clear cache: ${error instanceof Error ? error.message : String(error)}`
    };
  }
}

/**
 * Complete fix that handles both profile update and cache clearing
 */
export async function completeAdminFix(): Promise<FixResult> {
  console.log('🚀 Starting complete admin fix...');
  console.log('=====================================');
  
  try {
    // Step 1: Clear problematic cache
    const cacheResult = await clearAuthCache();
    if (!cacheResult.success) {
      console.warn('⚠️ Cache clearing failed:', cacheResult.message);
    } else {
      console.log('✅ Cache cleared successfully');
    }
    
    // Step 2: Fix admin profile
    const profileResult = await fixAdminProfile();
    if (!profileResult.success) {
      return {
        success: false,
        message: `Profile fix failed: ${profileResult.message}`,
        details: { cacheResult, profileResult }
      };
    }
    
    console.log('✅ Profile fixed successfully');
    
    // Step 3: Final verification
    console.log('🔍 Final verification...');
    const { client } = createAdminClient();
    
    const { data: finalProfile } = await client
      .from('profiles')
      .select('role, subscription_status, email')
      .eq('email', 'simmakin@gmail.com')
      .single();
    
    console.log('✅ Final profile state:', finalProfile);
    
    return {
      success: true,
      message: 'Complete admin fix successful! Please refresh the page.',
      details: {
        cacheResult,
        profileResult,
        finalProfile
      }
    };
    
  } catch (error) {
    console.error('❌ Error in complete fix:', error);
    return {
      success: false,
      message: `Complete fix failed: ${error instanceof Error ? error.message : String(error)}`
    };
  }
}

// Make functions available globally for console use
if (typeof window !== 'undefined') {
  (window as any).fixAdminProfile = fixAdminProfile;
  (window as any).clearAuthCache = clearAuthCache;
  (window as any).completeAdminFix = completeAdminFix;
  
  console.log('🔧 Admin fix functions available:');
  console.log('- fixAdminProfile()');
  console.log('- clearAuthCache()');
  console.log('- completeAdminFix()');
} 